// Part of taxPages, split to keep files short; ../taxPages.ts merges the parts. Keys sit directly under taxPages.
export const en = {
  vatTabs: {
    ariaLabel: 'VAT',
    returns: 'Returns',
    rates: 'Tax rates',
    settings: 'Settings',
    statusOpen: 'Open',
    statusDueToday: 'Due today',
    statusDueIn_one: 'Due in {{count}} day',
    statusDueIn_other: 'Due in {{count}} days',
    statusOverdue: 'Overdue',
    statusFiled: 'Filed',
    statusPaid: 'Paid',
  },
  vatReport: {
    back: 'Back to report list',
    heading: '{{title}} report',
    period: 'Report period',
    custom: 'Custom',
    presets: {
      thisQuarter: 'This quarter',
      lastQuarter: 'Last quarter',
      thisMonth: 'This month',
      lastMonth: 'Last month',
      thisYear: 'This year',
      lastYear: 'Last year',
    },
    from: 'From',
    to: 'To',
    export: 'Export to Excel (CSV)',
    print: 'Print',
    trn: 'TRN {{trn}}',
    footer: 'Accrual basis · Posted transactions with a VAT code',
    footerGenerated: 'Accrual basis · Posted transactions with a VAT code · Generated {{at}}',
  },
  corporateTax: {
    title: 'Corporate tax',
    description: 'Worksheet from the books for the tax adviser. Nothing here is posted or filed.',
    financialYear: 'Financial year',
    export: 'Export to Excel (CSV)',
    print: 'Print',
    loadError: 'Could not load the worksheet',
    taxPeriod: 'Tax period',
    dueDate: 'Return and payment due',
    trn: 'TRN',
    yearNotOver: 'The year is not over: figures to date.',
    adjustments: 'Adjustments',
    adjustmentsHint: 'Add-backs and deductions from accounting profit, each with a reason.',
    addAdjustment: 'Add adjustment',
    noAdjustments: 'No adjustments yet.',
    suggested: 'Suggested from the accounts',
    add: 'Add',
    kinds: { add_back: 'Add-back', deduction: 'Deduction' },
    categories: {
      entertainment: 'Entertainment (50% non-deductible)',
      fines_penalties: 'Fines and penalties',
      donations: 'Donations',
      related_party: 'Related-party / transfer pricing',
      exempt_dividends: 'Exempt dividends',
      exempt_gains: 'Exempt gains (participation exemption)',
      interest_limitation: 'Interest limitation',
      depreciation: 'Depreciation and fixed assets',
      provisions: 'Provisions',
      other: 'Other',
    },
    computation: {
      title: 'Computation',
      revenue: 'Revenue',
      accountingProfit: 'Accounting profit',
      accountingProfitHint: 'Income less expenses for the year, before the corporate tax charge',
      addBacks: 'Add: add-backs',
      deductions: 'Less: deductions',
      adjustedProfit: 'Adjusted profit',
      lossRelief: 'Less: tax losses brought forward',
      lossReliefHint: 'At most 75% of taxable income',
      taxableIncome: 'Taxable income',
      sbrNil: 'Small business relief: taxable income treated as nil',
      qualifyingAtZero: 'Qualifying income at 0%',
      nonQualifyingAtRate: 'Non-qualifying income at {{rate}}%',
      zeroBand: 'At 0% (first {{currency}} 375,000)',
      atRate: 'At {{rate}}%',
      payable: 'Corporate tax payable',
    },
    elections: {
      title: 'Reliefs and free zone',
      edit: 'Edit reliefs and free zone',
      sbr: 'Small business relief',
      sbrApplied: 'Elected and applied',
      sbrNotAvailable: 'Elected, not available',
      notElected: 'Not elected',
      sbrEligible: 'available: revenue is within AED 3,000,000',
      sbrIneligible: 'not available: {{reasons}}',
      qfzp: 'Qualifying free zone person',
      qfzpApplied: 'Elected: qualifying income at 0%',
      qfzpNotMet: 'Elected, but the de minimis test is not met',
      notElectedFreeZone: 'Not elected (free zone company)',
      qualifyingRevenue: 'Qualifying revenue',
      nonQualifyingRevenue: 'Non-qualifying revenue',
      deMinimis: 'De minimis limit (lower of 5% and AED 5m)',
      tagged: 'Tagged:',
      accountTag: '{{name}} (account)',
      losses: 'Tax losses brought forward',
      notes: 'Notes for the adviser',
    },
    table: {
      type: 'Type',
      category: 'Category',
      account: 'Account',
      reason: 'Reason',
      amount: 'Amount',
      edit: 'Edit adjustment',
      remove: 'Remove adjustment',
      removed: 'Adjustment removed.',
    },
    pnl: {
      title: 'Profit and loss for the year',
      show: 'Show accounts',
      hide: 'Hide accounts',
      income: 'Income',
      totalIncome: 'Total income',
      expenses: 'Expenses',
      excluded: 'Corporate tax charge: excluded from accounting profit',
      totalExpenses: 'Total expenses (excluding corporate tax)',
      accountingProfit: 'Accounting profit',
    },
    adjustDialog: {
      saved: 'Adjustment saved.',
      added: 'Adjustment added.',
      editTitle: 'Edit adjustment',
      addTitle: 'Add adjustment',
      description: 'Changes taxable income on this worksheet only; the books are not touched.',
      type: 'Type',
      addBackOption: 'Add-back (increases taxable income)',
      deductionOption: 'Deduction (reduces taxable income)',
      category: 'Category',
      account: 'Account (optional)',
      accountAria: 'Account',
      amount: 'Amount ({{currency}})',
      reason: 'Reason',
    },
    electionsDialog: {
      saved: 'Worksheet saved.',
      title: 'Reliefs and free zone · {{year}}',
      description:
        'Small business relief and the qualifying free zone person regime cannot both apply.',
      electSbr: 'Elect small business relief',
      qfzp: 'Qualifying free zone person',
      qualifyingIncome: 'Qualifying income',
      qualifyingHint:
        'Tag whole income accounts, or customers whose income qualifies (for example free zone and overseas clients).',
      customers: 'Customers',
      addCustomerAria: 'Add a qualifying customer',
      addCustomer: 'Add a customer',
      removeCustomer: 'Remove {{name}}',
      losses: 'Tax losses brought forward ({{currency}})',
      notes: 'Notes for the adviser',
    },
  },
};

export const ar = {
  vatTabs: {
    ariaLabel: 'ضريبة القيمة المضافة',
    returns: 'الإقرارات',
    rates: 'نسب الضريبة',
    settings: 'الإعدادات',
    statusOpen: 'مفتوحة',
    statusDueToday: 'مستحق اليوم',
    statusDueIn_zero: 'مستحق اليوم',
    statusDueIn_one: 'مستحق خلال يوم واحد',
    statusDueIn_two: 'مستحق خلال يومين',
    statusDueIn_few: 'مستحق خلال {{count}} أيام',
    statusDueIn_many: 'مستحق خلال {{count}} يوماً',
    statusDueIn_other: 'مستحق خلال {{count}} يوم',
    statusOverdue: 'متأخر',
    statusFiled: 'مقدَّم',
    statusPaid: 'مدفوع',
  },
  vatReport: {
    back: 'العودة إلى قائمة التقارير',
    heading: 'تقرير {{title}}',
    period: 'فترة التقرير',
    custom: 'مخصصة',
    presets: {
      thisQuarter: 'هذا الربع',
      lastQuarter: 'الربع السابق',
      thisMonth: 'هذا الشهر',
      lastMonth: 'الشهر السابق',
      thisYear: 'هذه السنة',
      lastYear: 'السنة السابقة',
    },
    from: 'من',
    to: 'إلى',
    export: 'تصدير إلى Excel ‏(CSV)',
    print: 'طباعة',
    trn: 'رقم التسجيل الضريبي {{trn}}',
    footer: 'أساس الاستحقاق · المعاملات المرحَّلة التي لها رمز ضريبة',
    footerGenerated: 'أساس الاستحقاق · المعاملات المرحَّلة التي لها رمز ضريبة · أُنشئ في {{at}}',
  },
  corporateTax: {
    title: 'ضريبة الشركات',
    description: 'ورقة عمل من الدفاتر للمستشار الضريبي. لا يُرحَّل أو يُقدَّم أي شيء هنا.',
    financialYear: 'السنة المالية',
    export: 'تصدير إلى Excel ‏(CSV)',
    print: 'طباعة',
    loadError: 'تعذّر تحميل ورقة العمل',
    taxPeriod: 'الفترة الضريبية',
    dueDate: 'موعد تقديم الإقرار والسداد',
    trn: 'رقم التسجيل الضريبي',
    yearNotOver: 'السنة لم تنتهِ بعد: الأرقام حتى تاريخه.',
    adjustments: 'التعديلات',
    adjustmentsHint: 'الإضافات والخصومات على الربح المحاسبي، ولكل منها سبب.',
    addAdjustment: 'إضافة تعديل',
    noAdjustments: 'لا توجد تعديلات بعد.',
    suggested: 'مقترحة من الحسابات',
    add: 'إضافة',
    kinds: { add_back: 'إضافة', deduction: 'خصم' },
    categories: {
      entertainment: 'الضيافة والترفيه (50% غير قابلة للخصم)',
      fines_penalties: 'الغرامات والجزاءات',
      donations: 'التبرعات',
      related_party: 'الأطراف المرتبطة / أسعار التحويل',
      exempt_dividends: 'توزيعات الأرباح المعفاة',
      exempt_gains: 'الأرباح المعفاة (إعفاء المشاركة)',
      interest_limitation: 'تقييد خصم الفوائد',
      depreciation: 'الإهلاك والأصول الثابتة',
      provisions: 'المخصصات',
      other: 'أخرى',
    },
    computation: {
      title: 'الاحتساب',
      revenue: 'الإيرادات',
      accountingProfit: 'الربح المحاسبي',
      accountingProfitHint: 'الدخل ناقص المصروفات للسنة، قبل مصروف ضريبة الشركات',
      addBacks: 'يُضاف: الإضافات',
      deductions: 'يُطرح: الخصومات',
      adjustedProfit: 'الربح المعدَّل',
      lossRelief: 'يُطرح: الخسائر الضريبية المرحَّلة',
      lossReliefHint: 'بحد أقصى 75% من الدخل الخاضع للضريبة',
      taxableIncome: 'الدخل الخاضع للضريبة',
      sbrNil: 'إعفاء الأعمال الصغيرة: يُعامل الدخل الخاضع للضريبة على أنه صفر',
      qualifyingAtZero: 'الدخل المؤهل بنسبة 0%',
      nonQualifyingAtRate: 'الدخل غير المؤهل بنسبة {{rate}}%',
      zeroBand: 'بنسبة 0% (أول {{currency}} 375,000)',
      atRate: 'بنسبة {{rate}}%',
      payable: 'ضريبة الشركات المستحقة',
    },
    elections: {
      title: 'الإعفاءات والمنطقة الحرة',
      edit: 'تعديل الإعفاءات والمنطقة الحرة',
      sbr: 'إعفاء الأعمال الصغيرة',
      sbrApplied: 'تم اختياره وتطبيقه',
      sbrNotAvailable: 'تم اختياره، وهو غير متاح',
      notElected: 'لم يتم اختياره',
      sbrEligible: 'متاح: الإيرادات في حدود AED 3,000,000',
      sbrIneligible: 'غير متاح: {{reasons}}',
      qfzp: 'الشخص المؤهل في المنطقة الحرة',
      qfzpApplied: 'تم اختياره: الدخل المؤهل بنسبة 0%',
      qfzpNotMet: 'تم اختياره، لكن اختبار الحد الأدنى غير مستوفى',
      notElectedFreeZone: 'لم يتم اختياره (شركة في منطقة حرة)',
      qualifyingRevenue: 'الإيرادات المؤهلة',
      nonQualifyingRevenue: 'الإيرادات غير المؤهلة',
      deMinimis: 'حد الحد الأدنى (الأقل من 5% وAED 5 ملايين)',
      tagged: 'المحدَّد:',
      accountTag: '{{name}} (حساب)',
      losses: 'الخسائر الضريبية المرحَّلة',
      notes: 'ملاحظات للمستشار',
    },
    table: {
      type: 'النوع',
      category: 'الفئة',
      account: 'الحساب',
      reason: 'السبب',
      amount: 'المبلغ',
      edit: 'تعديل التعديل',
      remove: 'حذف التعديل',
      removed: 'تم حذف التعديل.',
    },
    pnl: {
      title: 'الأرباح والخسائر للسنة',
      show: 'إظهار الحسابات',
      hide: 'إخفاء الحسابات',
      income: 'الدخل',
      totalIncome: 'إجمالي الدخل',
      expenses: 'المصروفات',
      excluded: 'مصروف ضريبة الشركات: مستبعد من الربح المحاسبي',
      totalExpenses: 'إجمالي المصروفات (باستثناء ضريبة الشركات)',
      accountingProfit: 'الربح المحاسبي',
    },
    adjustDialog: {
      saved: 'تم حفظ التعديل.',
      added: 'تمت إضافة التعديل.',
      editTitle: 'تعديل بند التعديل',
      addTitle: 'إضافة تعديل',
      description: 'يغيّر الدخل الخاضع للضريبة في ورقة العمل هذه فقط؛ ولا تتأثر الدفاتر.',
      type: 'النوع',
      addBackOption: 'إضافة (تزيد الدخل الخاضع للضريبة)',
      deductionOption: 'خصم (يقلل الدخل الخاضع للضريبة)',
      category: 'الفئة',
      account: 'الحساب (اختياري)',
      accountAria: 'الحساب',
      amount: 'المبلغ ({{currency}})',
      reason: 'السبب',
    },
    electionsDialog: {
      saved: 'تم حفظ ورقة العمل.',
      title: 'الإعفاءات والمنطقة الحرة · {{year}}',
      description: 'لا يمكن تطبيق إعفاء الأعمال الصغيرة ونظام الشخص المؤهل في المنطقة الحرة معاً.',
      electSbr: 'اختيار إعفاء الأعمال الصغيرة',
      qfzp: 'الشخص المؤهل في المنطقة الحرة',
      qualifyingIncome: 'الدخل المؤهل',
      qualifyingHint:
        'حدّد حسابات دخل كاملة، أو عملاء يكون دخلهم مؤهلاً (مثل العملاء في المناطق الحرة وخارج الدولة).',
      customers: 'العملاء',
      addCustomerAria: 'إضافة عميل مؤهل',
      addCustomer: 'إضافة عميل',
      removeCustomer: 'إزالة {{name}}',
      losses: 'الخسائر الضريبية المرحَّلة ({{currency}})',
      notes: 'ملاحظات للمستشار',
    },
  },
};
